import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { api } from '../api';
import { useResource } from './use-resource';
import { formatMoney } from '@bookingdei/i18n/format-money';
import { useLanguage } from './language-context';
import type { PublicSettings } from '../mock/seeds/schema-types';

// Loads the public settings subset once (currency, hold minutes, feature flags —
// API-27) and exposes a money formatter driven by the registry currency, never a
// hardcoded symbol (INF-1). Until it resolves, money falls back to a neutral
// minor-unit render. Duplicated per surface (CB-D3).
interface SettingsContextValue {
  settings: PublicSettings | undefined;
  loading: boolean;
  money: (minor: number | undefined | null) => string;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const { lang } = useLanguage();
  const { data, loading } = useResource<PublicSettings>(
    (signal) => api.GET('/registries/settings', { signal, lang }),
    [lang],
  );

  const value = useMemo<SettingsContextValue>(() => {
    const currency = data?.currency;
    const scale = currency?.minor_unit_scale ?? 100;
    const symbol = currency?.symbol ?? currency?.code ?? '';
    const money = (minor: number | undefined | null) => {
      if (minor === undefined || minor === null) return '—';
      // bn: Bangla digits + South Asian grouping through packages/i18n (DR-49, CB-15).
      if (lang === 'bn') return formatMoney(lang, minor, { currencySymbol: symbol, minorUnitScale: scale });
      const major = minor / scale;
      const formatted = major.toLocaleString('en-US', {
        maximumFractionDigits: major % 1 === 0 ? 0 : 2,
      });
      return symbol ? `${symbol}${formatted}` : formatted;
    };
    return { settings: data, loading, money };
  }, [data, loading, lang]);

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings(): SettingsContextValue {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within a SettingsProvider');
  return ctx;
}
